import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { Label, TextInput } from '@/shared/ui';
import { FieldError } from './FieldError';
import { labelStyle } from './drawer-styles';
import { maskCPF, maskCNPJ, maskIssuingAgency, maskRG } from '../../utils/masks';
import type { ClientFormData } from './schema';

interface IdentityExtraSectionProps {
  form: UseFormReturn<ClientFormData>;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Document fields: CPF/RG (individual) or CNPJ/trade name (company).
 * Collapsed by default in creation — optional for quick registration.
 */
export const IdentityExtraSection: React.FC<IdentityExtraSectionProps> = ({
  form,
  collapsed,
  onToggle,
}) => {
  const { t } = useTranslation('features/clients');
  const personType = form.watch('identity.personType');

  return (
    <FormSection
      title={t('drawer.identityExtra.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      {personType === 'individual' ? (
        <>
          <Controller
            name="identity.cpf"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>CPF</Label>
                <TextInput
                  {...field}
                  placeholder="000.000.000-00"
                  onChange={(e) => field.onChange(maskCPF(e.target.value))}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
          <Controller
            name="identity.rg"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>{t('drawer.identityExtra.rg')}</Label>
                <TextInput
                  {...field}
                  placeholder={t('drawer.identityExtra.rgPlaceholder')}
                  maxLength={20}
                  onChange={(e) => field.onChange(maskRG(e.target.value))}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
          <Controller
            name="identity.issuingAgency"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>{t('drawer.identityExtra.issuingAgency')}</Label>
                <TextInput
                  {...field}
                  placeholder={t('drawer.identityExtra.issuingAgencyPlaceholder')}
                  maxLength={20}
                  onChange={(e) => field.onChange(maskIssuingAgency(e.target.value))}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
        </>
      ) : (
        <>
          <Controller
            name="identity.cnpj"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>CNPJ</Label>
                <TextInput
                  {...field}
                  placeholder="00.000.000/0000-00"
                  onChange={(e) => field.onChange(maskCNPJ(e.target.value))}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
          <Controller
            name="identity.corporateName"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>{t('drawer.identityExtra.corporateName')}</Label>
                <TextInput
                  {...field}
                  placeholder={t('drawer.identityExtra.corporateNamePlaceholder', 'Razão Social')}
                  maxLength={150}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
          <Controller
            name="identity.tradeName"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <Label style={labelStyle}>{t('drawer.identityExtra.tradeName')}</Label>
                <TextInput
                  {...field}
                  placeholder={t('drawer.identityExtra.tradeNamePlaceholder')}
                  maxLength={150}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
        </>
      )}
    </FormSection>
  );
};
